import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import {
  IconButton,
  IconMoreVert,
  Divider,
  Menu,
  MenuListItem,
  TableContainer,
  TableContent,
  Table,
  TOAST_VARIANTS,
  Toast,
  TOAST_POSITIONS,
  TOAST_VISIBILITY_DURATIONS
} from 'cdk-radial';
import './ViewLocations.scss';
import { NoDataOverlay } from '../../../../utils/NoDataOverlay';
import { resetDaabSubscriptionsState } from '../../../../../../redux/connectedComponent/daabSubscriptions/daabSubscriptions.slice';
import { CustomMenu } from './CustomMenu';

const renderCMF = (gridParams) => {
  const { data } = gridParams;
  return <div className="view-locations-table-cell">{data.cmf || 'N/A'}</div>;
};

const renderName = (gridParams) => {
  const { data } = gridParams;
  return (
    <div className="view-locations-table-cell view-location-name">
      {data.entityName}
    </div>
  );
};

const renderAddress = (gridParams) => {
  const { data } = gridParams;
  return <div className="view-locations-table-cell">{data.address}</div>;
};

const toMutableRows = (rows = []) => {
  return Array.isArray(rows)
    ? rows.map((row) => ({ ...(row || {}) }))
    : [];
};

export const LocationTable = ({ locations, searchInput, status, apiDms, apiData, app }) => {
  const renderMenu = (gridParams) => {
    return <CustomMenu gridParams={gridParams} apiDms={apiDms} apiData={apiData} app={app} />;
  };

  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState(() => toMutableRows(locations));
  const [toastDetails, setToastDetails] = useState({});
  const [showToast, setShowToast] = useState(false);

  const dispatch = useDispatch();

  const handleDismissToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const state = useSelector((state) => state);
    const {
      subscribeRequestSent,
      errorSendingSubscribeRequest,
    } = state.daabSubscriptionsSlice;

  useEffect(() => {
    const sourceRows = toMutableRows(locations);
    if (searchInput) {
      const filteredData = sourceRows.filter(
        (location) =>
          location.entityName
            ?.toLowerCase()
            .includes(searchInput.toLowerCase()) ||
          location.cmf?.includes(searchInput)
      );
      setTableData(toMutableRows(filteredData));
    } else {
      setTableData(sourceRows);
    }
  }, [locations, searchInput]);

  const columns = [
    {
      cellRendererFramework: renderName,
      field: 'entityName',
      headerName: 'Dealership Locations',
      width: 420,
      minWidth: 200,
    },
    {
      cellRendererFramework: renderAddress,
      field: 'address',
      headerName: 'Address',
      width: 480,
      minWidth: 200,
    },
    {
      cellRendererFramework: renderCMF,
      field: 'cmf',
      headerName: 'CMF Number',
      width: status === 'active' && apiDms ? 260 : 330,
      minWidth: 100,
    },
    {
      cellRendererFramework: renderMenu,
      field: 'menu',
      headerName: '',
      width: 70,
      minWidth: 70,
      hide: status === 'active' && apiDms ? false : true,
    },
  ];

  const handleGridReady = (gridParams) => {
    setGridApi(gridParams.api);
  };

  useEffect(() => {
      if (subscribeRequestSent) {
        setToastDetails({
          variant: TOAST_VARIANTS.POSITIVE,
          toastContent: 'Your request for DMS update is accepted. Come back later to check the status.',
        });
        setShowToast(true);
      }
    }, [subscribeRequestSent]);
  
    useEffect(() => {
      if (errorSendingSubscribeRequest) {
        setToastDetails({
          variant: TOAST_VARIANTS.NEGATIVE,
          toastContent: 'Your request for DMS update could not be processed. Try again after some time.'
        });
        setShowToast(true);
      }
    }, [errorSendingSubscribeRequest]);

  return (
    <div>
        <Divider style={{ marginTop: '10px' }} />
        <TableContainer className="view-locations-table">
          <TableContent
            className="ag-theme-balham"
            data-testid="view-locations-table"
          >
            <Table
              columnDefs={columns}
              rowHeight={52}
              isSearchable={false}
              isDownloadable={false}
              isPrintable={false}
              resizableHeaders={true}
              showNoRowsOverlay={false}
              showFooter={false}
              hideTableTitle={true}
              hideBorder={true}
              hasPagination={false}
              onGridReady={handleGridReady}
              immutableData={true}
              getRowNodeId={(row) => row?.id || row?.entityId || row?.cmf || JSON.stringify(row)}
              rowData={tableData}
              frameworkComponents={{
                customNoRowsOverlay: (params) => {
                  return (
                    <NoDataOverlay
                      message={`There are no ${status} locations.`}
                      className={'no-rows-display'}
                    />
                  );
                },
              }}
              noRowsOverlayComponent="customNoRowsOverlay"
            />
          </TableContent>
        </TableContainer>
        {showToast && (
        <Toast
          variant={toastDetails.variant}
          content={toastDetails.toastContent}
          id={'save-dms-toast'}
          actions={[
            {
              onClick: handleDismissToast,
              text: 'Dismiss',
            },
          ]}
          onVisibilityDurationEnd={handleDismissToast}
          position={TOAST_POSITIONS.FIXED}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          style={{ marginTop: '64px' }}
        />
      )}
    </div>
  );
};
